import { useCallback, useEffect, useState } from 'react'
import { GetOrdersHistoryParams, OrderListItem } from '../../services/api/orders/models'
import {
  mapOrderDetailResponseToListItem,
  ordersApi,
} from '../../services/api/orders/orders'
import { ApiError } from '../../services/api/api-error-data'
import { defaultItemsCount } from '../../settings/home-page-settings'

const initialQuery: GetOrdersHistoryParams = {
  pageIndex: 0,
  pageSize: defaultItemsCount,
  sortByDate: 1,
}

export const useOrdersListPage = (activeTab: 0 | 1) => {
  const [purchasedOrders, setPurchasedOrders] = useState<OrderListItem[]>([])
  const [soldOrders, setSoldOrders] = useState<OrderListItem[]>([])
  const [loadingPurchased, setLoadingPurchased] = useState(false)
  const [loadingSold, setLoadingSold] = useState(false)
  const [errorPurchased, setErrorPurchased] = useState<ApiError | null>(null)
  const [errorSold, setErrorSold] = useState<ApiError | null>(null)
  const [purchasedQuery, setPurchasedQuery] =
    useState<GetOrdersHistoryParams>(initialQuery)
  const [soldQuery, setSoldQuery] =
    useState<GetOrdersHistoryParams>(initialQuery)
  const [purchasedTotalPages, setPurchasedTotalPages] = useState(0)
  const [soldTotalPages, setSoldTotalPages] = useState(0)

  useEffect(() => {
    const cancelled = { current: false }
    setLoadingPurchased(true)
    setErrorPurchased(null)
    ordersApi
      .getPurchaseHistory(purchasedQuery)
      .then((paged) => {
        if (cancelled.current) return
        setPurchasedOrders(
          paged.values.map((row) => mapOrderDetailResponseToListItem(row))
        )
        setPurchasedTotalPages(paged.totalPages)
      })
      .catch((e) => {
        if (cancelled.current) return
        setErrorPurchased(new ApiError(e))
      })
      .finally(() => {
        if (!cancelled.current) setLoadingPurchased(false)
      })
    return () => {
      cancelled.current = true
    }
  }, [purchasedQuery])

  useEffect(() => {
    const cancelled = { current: false }
    setLoadingSold(true)
    setErrorSold(null)
    ordersApi
      .getSalesHistory(soldQuery)
      .then((paged) => {
        if (cancelled.current) return
        setSoldOrders(
          paged.values.map((row) => mapOrderDetailResponseToListItem(row))
        )
        setSoldTotalPages(paged.totalPages)
      })
      .catch((e) => {
        if (cancelled.current) return
        setErrorSold(new ApiError(e))
      })
      .finally(() => {
        if (!cancelled.current) setLoadingSold(false)
      })
    return () => {
      cancelled.current = true
    }
  }, [soldQuery])

  const onPurchasedPageChange = useCallback((pageIndex: number) => {
    setPurchasedQuery((q) => ({ ...q, pageIndex }))
  }, [])

  const onSoldPageChange = useCallback((pageIndex: number) => {
    setSoldQuery((q) => ({ ...q, pageIndex }))
  }, [])

  const onPurchasedSortChange = useCallback((sortByDate: number) => {
    setPurchasedQuery((q) => ({ ...q, sortByDate, pageIndex: 0 }))
  }, [])

  const onSoldSortChange = useCallback((sortByDate: number) => {
    setSoldQuery((q) => ({ ...q, sortByDate, pageIndex: 0 }))
  }, [])

  return {
    purchasedOrders,
    soldOrders,
    loadingPurchased,
    loadingSold,
    errorPurchased,
    errorSold,
    purchasedQuery,
    soldQuery,
    purchasedTotalPages,
    soldTotalPages,
    loading: activeTab === 0 ? loadingPurchased : loadingSold,
    error: activeTab === 0 ? errorPurchased : errorSold,
    onPurchasedPageChange,
    onSoldPageChange,
    onPurchasedSortChange,
    onSoldSortChange,
  }
}
